// ===========================================
// Cost Overview Tile - Compact Cost Summary
// ===========================================
// A compact stat tile for the dashboard showing total costs
// Includes both project costs and global subscriptions (Cursor, ChatGPT, etc.)

import React, { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { DollarSign } from 'lucide-react';
import './CostOverviewTile.scss';

const CostOverviewTile = ({ projects }) => {
  // Get user data for global subscriptions
  const { user } = useAuth();

  // Calculate all cost analytics from projects and global subscriptions
  const analytics = useMemo(() => {
    // Initialize defaults
    const data = {
      projectCosts: 0,      // Total monthly cost from project services
      globalCosts: 0,       // Total monthly cost from global subscriptions
      totalMonthly: 0,      // Combined total
      totalYearly: 0,       // Annualized total
      projectsWithCosts: 0, // Number of projects with costs
      freeProjects: 0,      // Number of projects without costs
      subscriptionCount: 0  // Number of active global subscriptions
    };

    // Calculate project costs
    if (projects && projects.length > 0) {
      projects.forEach(project => {
        const cost = project.totalMonthlyCost || 0;
        data.projectCosts += cost;
        
        if (cost > 0) {
          data.projectsWithCosts += 1;
        } else {
          data.freeProjects += 1;
        }
      });
    }

    // Calculate global subscription costs (from user profile)
    if (user?.globalSubscriptions && user.globalSubscriptions.length > 0) {
      user.globalSubscriptions.forEach(sub => {
        if (sub.isActive) {
          // Convert yearly billing to monthly equivalent
          const monthly = sub.billingCycle === 'yearly' 
            ? sub.monthlyCost / 12 
            : sub.monthlyCost;
          data.globalCosts += monthly;
          data.subscriptionCount += 1;
        }
      });
    }

    // Calculate totals
    data.totalMonthly = data.projectCosts + data.globalCosts;
    data.totalYearly = data.totalMonthly * 12;

    return data;
  }, [projects, user?.globalSubscriptions]);

  return (
    <div className="cost-tile">
      {/* Icon */}
      <div className="cost-tile__icon" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
        <DollarSign size={22} style={{ color: 'var(--color-success)' }} />
      </div>
      
      {/* Main Content - Monthly/Yearly costs */}
      <div className="cost-tile__content">
        <div className="cost-tile__row">
          <span className="cost-tile__value">${analytics.totalMonthly.toFixed(0)}</span>
          <span className="cost-tile__yearly">/ ${analytics.totalYearly.toFixed(0)} yr</span>
        </div>
        <span className="cost-tile__label">Cost Overview</span>
      </div>
      
      {/* Mini Stats - breakdown */}
      <div className="cost-tile__mini-stats">
        {/* Projects with costs */}
        <div className="cost-tile__mini">
          <span className="cost-tile__mini-value">{analytics.projectsWithCosts}</span>
          <span className="cost-tile__mini-label">Paid</span>
        </div>
        {/* Free projects */}
        <div className="cost-tile__mini">
          <span className="cost-tile__mini-value">{analytics.freeProjects}</span>
          <span className="cost-tile__mini-label">Free</span>
        </div>
        {/* Global subscriptions */}
        <div className="cost-tile__mini">
          <span className="cost-tile__mini-value">{analytics.subscriptionCount}</span>
          <span className="cost-tile__mini-label">Subs</span>
        </div>
      </div>
    </div>
  );
};

export default CostOverviewTile;

